import { CategoryTypes } from "../../../interfaces/enums";
import { CategoryWithId } from "../../../interfaces/ICategories";
import { ParticipantWithId } from "../../../interfaces/IParticipants";
import { PresentationFormState } from "../types/PresentationFormTypes";
import { PresentationWithId } from "../../../interfaces/IPresentations";
import { useApplicationContext } from "../../../contexts/ApplicationContext";
import { useCallback, useState } from "react";
import { useUserContext } from "../../../contexts/UserContext";
/**
 * ============================================================================
 * USE PRESENTATION FORM - GŁÓWNY HOOK ZARZĄDZAJĄCY STANEM FORMULARZA PREZENTACJI
 * ============================================================================
 *
 * CEL HOOKA:
 * - Centralne zarządzanie stanem formularza prezentacji (zawodnicy, dane formularza, walidacja)
 * - Obsługa logiki biznesowej dla różnych typów kategorii (SOLO, DUO, TRIO, OTHER)
 * - Generowanie nazw prezentacji automatycznie dla SOLO/DUO/TRIO
 * - Zarządzanie procesem submitowania prezentacji do bazy danych
 *
 * ARCHITEKTURA:
 * - Custom Hook Pattern: Enkapsuluje logikę formularza w reusable hook
 * - State Management: Zarządza lokalnym stanem formularza (selectedParticipants, formData)
 * - Business Logic: Implementuje reguły biznesowe dla różnych typów kategorii
 * - Data Transformation: Przekształca dane formularza w obiekty prezentacji
 *
 * PRZEPŁYW DANYCH:
 * 1. Inicjalizuje stan na podstawie initialData (tryb edycji) lub pustych wartości (tryb dodawania)
 * 2. Obsługuje zmiany w formularzu (handleFormDataChange, handleParticipantsChange)
 * 3. Waliduje dane przed submitowaniem (validateParticipants)
 * 4. Generuje nazwy prezentacji automatycznie dla SOLO/DUO/TRIO
 * 5. Tworzy obiekty prezentacji z odpowiednimi danymi (applicationId, contestId, categoryId, userId)
 * 6. Wywołuje onSubmit dla każdej prezentacji (SOLO może tworzyć wiele prezentacji)
 *
 * TYPY KATEGORII I ICH OBSŁUGA:
 * - SOLO: Każdy wybrany zawodnik = osobna prezentacja z automatyczną nazwą
 * - DUO/TRIO: Grupa zawodników = jedna prezentacja z automatyczną nazwą (bez pola nazwy)
 * - OTHER: Grupa zawodników = jedna prezentacja z ręczną nazwą (z polem nazwy)
 */


interface UsePresentationFormProps {
  initialData?: PresentationWithId;
  category: CategoryWithId | null;
  onSubmit: (presentation: PresentationWithId) => Promise<void>;
}

export const usePresentationForm = ({
  initialData,
  category,
  onSubmit,
}: UsePresentationFormProps) => {
  // ============================================================================
  // POBRANIE DANYCH Z KONTEKSTÓW
  // ============================================================================

  // Pobierz dane użytkownika (potrzebne do userId w prezentacji)
  const { user } = useUserContext();

  // Pobierz dane aplikacji (potrzebne do applicationId i contestId w prezentacji)
  const { application } = useApplicationContext();
  // ============================================================================
  // INICJALIZACJA STANU FORMULARZA
  // ============================================================================

  // Stan formularza zawiera:
  // - selectedParticipants: Lista wybranych zawodników do prezentacji
  // - formData: Dane tekstowe formularza (nazwa, tytuł choreografii)
  // - showAll: Flaga pokazująca wszystkich zawodników (ignoruje filtry)
  // - hasAttemptedSubmit: Flaga czy użytkownik próbował już submitować (dla walidacji)
  const [state, setState] = useState<PresentationFormState>({
    selectedParticipants: initialData?.participants || [],
    formData: {
      name: initialData?.name || "",
      title: initialData?.title || "",
    },
    showAll: false,
    hasAttemptedSubmit: false,
  });

  // ============================================================================
  // HANDLERY ZMIAN W FORMULARZU
  // ============================================================================

  // Obsługuje zmiany w polach tekstowych formularza (nazwa, tytuł)
  // Wejście: key (nazwa pola), value (nowa wartość)
  // Wyjście: Aktualizuje state.formData
  const handleFormDataChange = useCallback((key: string, value: string) => {
    setState((prev) => ({
      ...prev,
      formData: { ...prev.formData, [key]: value },
    }));
  }, []);

  // Obsługuje zmiany w liście wybranych zawodników
  // Wejście: participants (nowa lista zawodników)
  // Wyjście: Aktualizuje state.selectedParticipants
  const handleParticipantsChange = useCallback((participants: ParticipantWithId[]) => {
    setState((prev) => ({ ...prev, selectedParticipants: participants }));
  }, []);

  // Obsługuje przełączanie trybu "pokaż wszystkich zawodników"
  // Wejście: showAll (boolean)
  // Wyjście: Aktualizuje state.showAll (wpływa na filtrowanie zawodników)
  const handleShowAllChange = useCallback((showAll: boolean) => {
    setState((prev) => ({ ...prev, showAll }));
  }, []);

  // ============================================================================
  // FUNKCJE GENEROWANIA NAZW PREZENTACJI
  // ============================================================================

  // Generuje nazwę prezentacji SOLO na podstawie imienia i nazwiska zawodnika
  // Wejście: participant (obiekt zawodnika)
  // Wyjście: string (np. "Jan Kowalski")
  const generateSoloPresentationName = useCallback((participant: ParticipantWithId): string => {
    return `${participant.firstName} ${participant.lastName}`;
  }, []);

  // Generuje nazwę prezentacji grupowej na podstawie imion i nazwisk wszystkich zawodników
  // Wejście: participants (lista zawodników)
  // Wyjście: string (np. "Jan Kowalski, Anna Nowak")
  const generateGroupPresentationName = useCallback((participants: ParticipantWithId[]): string => {
    return participants
      .map((participant) => `${participant.firstName} ${participant.lastName}`)
      .join(", ");
  }, []);

  // ============================================================================
  // WALIDACJA LICZBY ZAWODNIKÓW
  // ============================================================================

  // Waliduje czy liczba wybranych zawodników jest zgodna z wymaganiami kategorii
  // Wejście: participants (lista zawodników do walidacji)
  // Wyjście: boolean (true jeśli liczba jest poprawna)
  // Reguły:
  // - SOLO: minimum 1 zawodnik
  // - DUO/TRIO/OTHER: między minPresentationSize a maxPresentationSize
  const validateParticipants = useCallback(
    (participants: ParticipantWithId[]): boolean => {
      if (!category) return true;

      const count = participants.length;

      if (category.type === CategoryTypes.SOLO) {
        return count > 0;
      }

      return count >= category.minPresentationSize && count <= category.maxPresentationSize;
    },
    [category]
  );

  // ============================================================================
  // GŁÓWNA FUNKCJA SUBMITOWANIA FORMULARZA
  // ============================================================================

  // Obsługuje proces submitowania formularza prezentacji
  // Wejście: Brak (używa state i props)
  // Wyjście: Promise<void> (wywołuje onSubmit dla każdej prezentacji)
  // Proces:
  // 1. Ustawia hasAttemptedSubmit = true (dla walidacji UI)
  // 2. Waliduje liczbę zawodników
  // 3. Tworzy obiekty prezentacji na podstawie typu kategorii
  // 4. Wywołuje onSubmit dla każdej prezentacji
  const handleSubmit = useCallback(async () => {
    // Ustaw flagę że użytkownik próbował submitować (dla walidacji UI)
    setState((prev) => ({ ...prev, hasAttemptedSubmit: true }));

    // Waliduj liczbę zawodników przed kontynuowaniem
    if (!validateParticipants(state.selectedParticipants)) {
      return;
    }

    if (category?.type === CategoryTypes.SOLO) {
      // ========================================================================
      // OBSŁUGA KATEGORII SOLO - KAŻDY ZAWODNIK TO OSOBNA PREZENTACJA
      // ========================================================================

      // Dla SOLO: każdy wybrany zawodnik tworzy osobną prezentację
      // Mapuje listę zawodników na listę prezentacji
      const presentations: PresentationWithId[] = state.selectedParticipants.map((participant) => ({
        ...initialData, // Zachowaj dane z initialData (tryb edycji)
        name: generateSoloPresentationName(participant), // Automatyczna nazwa z imienia i nazwiska
        title: "", // SOLO nie ma tytułu choreografii
        participants: [participant], // Jeden zawodnik na prezentację
        id: initialData?.id || "", // ID z initialData lub pusty (nowa prezentacja)
        applicationId: application?.id || "", // ID aplikacji z kontekstu
        contestId: application?.contestId || "", // ID turnieju z kontekstu
        categoryId: category?.id || "", // ID kategorii z props
        userId: user?.id || "", // ID użytkownika z kontekstu
        startNo: initialData?.startNo || 0, // Numer startowy z initialData lub 0
      }));

      // Dodaj wszystkie prezentacje SOLO (może być wiele)
      for (const presentation of presentations) {
        await onSubmit(presentation);
      }
    } else {
      // ========================================================================
      // OBSŁUGA KATEGORII GRUPOWYCH - GRUPA ZAWODNIKÓW TO JEDNA PREZENTACJA
      // ========================================================================

      // Dla DUO/TRIO/OTHER: grupa zawodników tworzy jedną prezentację
      const presentationData: PresentationWithId = {
        ...initialData, // Zachowaj dane z initialData (tryb edycji)
        name:
          category?.type === CategoryTypes.DUO || category?.type === CategoryTypes.TRIO
            ? generateGroupPresentationName(state.selectedParticipants) // DUO/TRIO: automatyczna nazwa
            : state.formData.name || generateGroupPresentationName(state.selectedParticipants), // OTHER: ręczna nazwa lub automatyczna
        title: state.formData.title || "", // Tytuł choreografii (może być wymagany)
        participants: state.selectedParticipants, // Wszyscy wybrani zawodnicy
        id: initialData?.id || "", // ID z initialData lub pusty (nowa prezentacja)
        applicationId: application?.id || "", // ID aplikacji z kontekstu
        contestId: application?.contestId || "", // ID turnieju z kontekstu
        categoryId: category?.id || "", // ID kategorii z props
        userId: user?.id || "", // ID użytkownika z kontekstu
        startNo: initialData?.startNo || 0, // Numer startowy z initialData lub 0
      };

      // Dodaj jedną prezentację grupową
      await onSubmit(presentationData);
    }
  }, [
    state,
    category,
    initialData,
    onSubmit,
    validateParticipants,
    generateSoloPresentationName,
    generateGroupPresentationName,
  ]);

  // ============================================================================
  // FUNKCJA GENEROWANIA TEKSTU PRZYCISKU SUBMIT
  // ============================================================================

  // Generuje tekst przycisku submit na podstawie typu kategorii i trybu (edycja/dodawanie)
  // Wejście: isEdit (boolean - czy to tryb edycji)
  // Wyjście: string (tekst przycisku)
  // Różne teksty dla różnych typów kategorii i trybów
  const getSubmitText = useCallback(
    (isEdit: boolean): string => {
      const participantsCount = state.selectedParticipants.length;

      if (category?.type === CategoryTypes.SOLO) {
        return isEdit
          ? `Zapisz ${participantsCount} prezentacji solo`
          : `Dodaj ${participantsCount} prezentacji solo`;
      }

      if (category?.type === CategoryTypes.DUO) {
        return isEdit ? "Zapisz duet" : "Dodaj duet";
      }

      if (category?.type === CategoryTypes.TRIO) {
        return isEdit ? "Zapisz trio" : "Dodaj trio";
      }

      return isEdit ? "Zapisz prezentację" : "Dodaj prezentację";
    },
    [category, state.selectedParticipants.length]
  );

  // ============================================================================
  // EKSPORT INTERFEJSU HOOKA
  // ============================================================================

  // Zwraca wszystkie funkcje i dane potrzebne do zarządzania formularzem
  // Używane przez komponenty formularzy do kontrolowania UI i logiki
  return {
    state, // Stan formularza (zawodnicy, dane, flagi)
    setState, // Funkcja do bezpośredniej zmiany stanu
    handleFormDataChange, // Handler zmian w polach tekstowych
    handleParticipantsChange, // Handler zmian w liście zawodników
    handleShowAllChange, // Handler przełączania trybu "pokaż wszystkich"
    handleSubmit, // Główna funkcja submitowania
    validateParticipants, // Funkcja walidacji liczby zawodników
    getSubmitText, // Funkcja generowania tekstu przycisku
  };
};
